// src/pages/UsersPage.tsx
import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Typography, message, Space, Tooltip, Tag } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, UserOutlined } from '@ant-design/icons';
import { mockUsers, mockClients } from '../data/mockData';
import { User, UserRole, UserStatus } from '../types';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>(mockUsers);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const getClientName = (clientId?: string) => {
    if (!clientId) return 'N/A';
    const client = mockClients.find(c => c.id === clientId);
    return client?.companyName || 'N/A';
  };

  const getRoleTag = (role: UserRole) => {
    return role === 'admin' 
      ? <Tag color="red">Administrador</Tag>
      : <Tag color="blue">Cliente</Tag>;
  };

  const getStatusTag = (status: UserStatus) => {
    const config = {
      'active': { color: 'success', text: 'Activo' },
      'inactive': { color: 'default', text: 'Inactivo' },
      'suspended': { color: 'error', text: 'Suspendido' },
    };
    const { color, text } = config[status];
    return <Tag color={color}>{text}</Tag>;
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => getRoleTag(role),
      filters: [
        { text: 'Administrador', value: 'admin' },
        { text: 'Cliente', value: 'client' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: UserStatus) => getStatusTag(status),
      filters: [
        { text: 'Activo', value: 'active' },
        { text: 'Inactivo', value: 'inactive' },
        { text: 'Suspendido', value: 'suspended' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Cliente',
      dataIndex: 'clientId',
      key: 'clientId',
      render: (clientId?: string) => getClientName(clientId),
    },
    {
      title: 'Último Acceso',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (date?: string) => date 
        ? new Date(date).toLocaleDateString('es-ES') 
        : 'Nunca',
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_: any, record: User) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewUser(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteUser(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleAddUser = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = (values: Omit<User, 'id' | 'createdAt'>) => {
    const newUser: User = {
      id: `u${users.length + 1}`,
      ...values,
      createdAt: new Date().toISOString(),
    };
    
    setUsers([...users, newUser]);
    message.success('Usuario creado exitosamente');
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    editForm.setFieldsValue(user);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (values: Partial<User>) => {
    if (!selectedUser) return;
    
    const updatedUsers = users.map(u => 
      u.id === selectedUser.id ? { ...u, ...values } : u
    );
    
    setUsers(updatedUsers);
    message.success('Usuario actualizado exitosamente');
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedUser(null);
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedUser(null);
  };

  const handleDeleteUser = (user: User) => {
    modal.confirm({
      title: '¿Está seguro de eliminar este usuario?',
      icon: <ExclamationCircleOutlined />,
      content: `Se eliminará el usuario "${user.name}". Esta acción no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        const updatedUsers = users.filter(u => u.id !== user.id);
        setUsers(updatedUsers);
        message.success('Usuario eliminado exitosamente');
      },
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>Gestión de Usuarios</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddUser}>
          Agregar Usuario
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {/* Modal de Creación */}
      <Modal
        title="Agregar Nuevo Usuario"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Nombre Completo"
            name="name"
            rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
          >
            <Input placeholder="Ej: Juan Pérez" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Por favor ingrese el email' },
              { type: 'email', message: 'Ingrese un email válido' }
            ]}
          >
            <Input placeholder="usuario@ejemplo.com" />
          </Form.Item>

          <Form.Item
            label="Teléfono"
            name="phone"
          >
            <Input placeholder="+57 300 1234567" />
          </Form.Item>

          <Form.Item
            label="Rol"
            name="role"
            rules={[{ required: true, message: 'Por favor seleccione un rol' }]}
          >
            <Select placeholder="Seleccione el rol">
              <Select.Option value="admin">Administrador</Select.Option>
              <Select.Option value="client">Cliente</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Estado"
            name="status"
            rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
            initialValue="active"
          >
            <Select placeholder="Seleccione el estado">
              <Select.Option value="active">Activo</Select.Option>
              <Select.Option value="inactive">Inactivo</Select.Option>
              <Select.Option value="suspended">Suspendido</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
          >
            {({ getFieldValue }) =>
              getFieldValue('role') === 'client' ? (
                <Form.Item
                  label="Cliente Asociado"
                  name="clientId"
                  rules={[{ required: true, message: 'Por favor seleccione un cliente' }]}
                >
                  <Select placeholder="Seleccione el cliente">
                    {mockClients.map(client => (
                      <Select.Option key={client.id} value={client.id}>
                        {client.companyName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Crear Usuario
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Edición */}
      <Modal
        title="Editar Usuario"
        open={isEditModalOpen}
        onCancel={handleCancelEdit}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            label="Nombre Completo"
            name="name"
            rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
          >
            <Input placeholder="Ej: Juan Pérez" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Por favor ingrese el email' },
              { type: 'email', message: 'Ingrese un email válido' }
            ]}
          >
            <Input placeholder="usuario@ejemplo.com" />
          </Form.Item>

          <Form.Item
            label="Teléfono"
            name="phone"
          >
            <Input placeholder="+57 300 1234567" />
          </Form.Item>

          <Form.Item
            label="Rol"
            name="role"
            rules={[{ required: true, message: 'Por favor seleccione un rol' }]}
          >
            <Select placeholder="Seleccione el rol">
              <Select.Option value="admin">Administrador</Select.Option>
              <Select.Option value="client">Cliente</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Estado"
            name="status"
            rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
          >
            <Select placeholder="Seleccione el estado">
              <Select.Option value="active">Activo</Select.Option>
              <Select.Option value="inactive">Inactivo</Select.Option>
              <Select.Option value="suspended">Suspendido</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
          >
            {({ getFieldValue }) =>
              getFieldValue('role') === 'client' ? (
                <Form.Item
                  label="Cliente Asociado"
                  name="clientId"
                  rules={[{ required: true, message: 'Por favor seleccione un cliente' }]}
                >
                  <Select placeholder="Seleccione el cliente">
                    {mockClients.map(client => (
                      <Select.Option key={client.id} value={client.id}>
                        {client.companyName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancelEdit}>Cancelar</Button>
              <Button type="primary" htmlType="submit">Guardar Cambios</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Detalles */}
      <Modal
        title="Detalles del Usuario"
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalOpen(false)}>
            Cerrar
          </Button>
        ]}
      >
        {selectedUser && (
          <div>
            <p><strong>Nombre:</strong> {selectedUser.name}</p>
            <p><strong>Email:</strong> {selectedUser.email}</p>
            <p><strong>Teléfono:</strong> {selectedUser.phone || 'No especificado'}</p>
            <p><strong>Rol:</strong> {getRoleTag(selectedUser.role)}</p>
            <p><strong>Estado:</strong> {getStatusTag(selectedUser.status)}</p>
            <p><strong>Cliente:</strong> {getClientName(selectedUser.clientId)}</p>
            <p><strong>Fecha de Creación:</strong> {new Date(selectedUser.createdAt).toLocaleString('es-ES')}</p>
            <p><strong>Último Acceso:</strong> {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleString('es-ES') : 'Nunca'}</p>
            <p><strong>ID:</strong> {selectedUser.id}</p>
          </div>
        )}
      </Modal>

      {contextHolder}
    </div>
  );
};

export default UsersPage;
